import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Popover,
  IconButton,
  Typography,
  Stack,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { format, subDays, startOfDay, endOfDay, isWithinInterval } from 'date-fns';
import { ru } from 'date-fns/locale';

const DateRangePicker = ({ 
  value = { 
    start: startOfDay(subDays(new Date(), 7)), 
    end: endOfDay(new Date()) 
  }, 
  onChange,
  disabled = false,
  size = 'medium',
  loading = false, // Добавляем проп loading
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempRange, setTempRange] = useState(value);
  const [currentMonth, setCurrentMonth] = useState(value.start || new Date());

  // Сброс временного диапазона при изменении значения
  useEffect(() => {
    setTempRange(value);
  }, [value]);

  
  
  const handleOpen = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    setTempRange(value);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (onChange && !loading) { // Проверяем loading
      onChange(tempRange);
    }
    handleClose();
    
  };

  const handleReset = () => {
    const today = new Date();
    const newRange = {
      start: startOfDay(subDays(today, 7)),
      end: endOfDay(today),
    };
    setTempRange(newRange);
    if (onChange) {
      onChange(newRange);
    }
  };

  const handleQuickSelect = (days) => {
    const today = new Date();
    let startDate;
    
    if (days === 0) {
      // Сегодня
      startDate = startOfDay(today);
    } else if (days === 1) {
      // Вчера
      startDate = startOfDay(subDays(today, 1));
    } else {
      // Последние N дней
      startDate = startOfDay(subDays(today, days - 1));
    }
    
    const newRange = {
      start: startDate,
      end: endOfDay(today),
    };
    
    setTempRange(newRange);
    if (onChange) {
      onChange(newRange);
    }
    handleClose();
  };

  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'dd.MM.yyyy', { locale: ru });
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    return format(date, 'dd.MM.yyyy HH:mm', { locale: ru });
  };

  const formatDisplay = () => {
    if (!value.start || !value.end) return 'Выберите период';
    
    const startStr = formatDate(value.start);
    const endStr = formatDate(value.end);
    
    if (startStr === endStr) {
      return startStr;
    }
    
    return `${startStr} - ${endStr}`;
  };

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    
    // Пустые ячейки для дней предыдущего месяца
    const firstDayWeekday = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    for (let i = 0; i < firstDayWeekday; i++) {
      days.push({ day: null, date: null });
    }
    
    // Дни текущего месяца
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({ day, date });
    }
    
    return days;
  };

  const handleDayClick = (date) => {
    if (!date || disabled) return;
    
    const clickedDate = startOfDay(date);
    
    if (!tempRange.start || (tempRange.start && tempRange.end)) {
      // Начинаем новый диапазон
      setTempRange({ 
        start: clickedDate, 
        end: null 
      });
    } else if (clickedDate < tempRange.start) {
      // Кликнули дату раньше начала
      setTempRange({ 
        start: clickedDate, 
        end: tempRange.start 
      });
    } else {
      // Завершаем диапазон
      setTempRange({ 
        ...tempRange, 
        end: endOfDay(clickedDate) 
      });
    }
  };

  const isDateInRange = (date) => {
    if (!tempRange.start || !date) return false;
    
    const start = startOfDay(tempRange.start);
    const end = tempRange.end ? endOfDay(tempRange.end) : start;
    const currentDate = startOfDay(date);
    
    return isWithinInterval(currentDate, { start, end });
  };

  const isRangeEdge = (date) => {
    if (!tempRange.start || !date) return false;
    
    const start = startOfDay(tempRange.start);
    const end = tempRange.end ? startOfDay(tempRange.end) : start;
    const currentDate = startOfDay(date);
    
    return currentDate.getTime() === start.getTime() ||
           (tempRange.end && currentDate.getTime() === end.getTime());
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  const calendarDays = generateCalendar();

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-picker' : undefined;

  return (
    <Box>
      {/* Кнопка/поле выбора */}
      <TextField
        value={formatDisplay()}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <>
              {loading && (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              )}
            <CalendarIcon sx={{ 
              color: 'action.active', 
              mr: 1,
              fontSize: size === 'small' ? '1rem' : '1.25rem',
            }} />
            </>
          ),
          endAdornment: disabled ? null : (
            <IconButton 
              size="small"
              onClick={handleOpen}
              sx={{ p: 0.5 }}
            >
              <DateRangeIcon />
            </IconButton>
          ),
        }}
        size={size}
        disabled={disabled}
        sx={{
          minWidth: isMobile ? 200 : 280,
          '& .MuiOutlinedInput-root': {
            pr: 1,
          },
        }}
      />

      {/* Popover с календарем */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            minWidth: 320,
            maxWidth: 400,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        {/* Заголовок с быстрым выбором */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            Быстрый выбор:
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {[1, 7, 30, 90].map((days) => (
              <Button
                key={days}
                size="small"
                variant="outlined"
                onClick={() => handleQuickSelect(days)}
                sx={{ borderRadius: 1 }}
              >
                {days === 1 ? 'Вчера' : `Последние ${days} дн.`}
              </Button>
            ))}
            <Button
              size="small"
              variant="outlined"
              onClick={handleReset}
              startIcon={<TodayIcon />}
              sx={{ borderRadius: 1 }}
            >
              Неделя
            </Button>
          </Stack>
        </Box>

        {/* Выбранный диапазон */}
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2, 
            mb: 2, 
            bgcolor: 'grey.50',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Выбранный период:
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {tempRange.start ? formatDateTime(tempRange.start) : '—'}
            {' → '}
            {tempRange.end ? formatDateTime(tempRange.end) : '—'}
          </Typography>
        </Paper>

        {/* Календарь */}
        <Box>
          {/* Навигация по месяцам */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2,
          }}>
            <IconButton 
              size="small" 
              onClick={prevMonth}
              sx={{ p: 0.5 }}
            >
              <ChevronLeftIcon />
            </IconButton>
            
            <Typography variant="h6" fontWeight={500}>
              {format(currentMonth, 'LLLL yyyy', { locale: ru })}
            </Typography>
            
            <IconButton 
              size="small" 
              onClick={nextMonth}
              sx={{ p: 0.5 }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Дни недели */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.5,
            mb: 1,
          }}>
            {weekDays.map((day) => (
              <Typography
                key={day}
                variant="caption"
                align="center"
                color="text.secondary"
                sx={{ py: 0.5 }}
              >
                {day}
              </Typography>
            ))}
          </Box>

          {/* Дни месяца */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.5,
          }}>
            {calendarDays.map(({ day, date }, index) => {
              const inRange = isDateInRange(date);
              const isEdge = isRangeEdge(date);
              const isToday = date && formatDate(date) === formatDate(new Date());
              
              return (
                <Button
                  key={index}
                  onClick={() => handleDayClick(date)}
                  disabled={!date}
                  sx={{
                    minWidth: 0,
                    minHeight: 0,
                    p: 0.5,
                    aspectRatio: '1',
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    bgcolor: isEdge 
                      ? 'primary.main' 
                      : inRange 
                        ? 'primary.light' 
                        : 'transparent',
                    color: isEdge 
                      ? 'primary.contrastText' 
                      : inRange 
                        ? 'primary.contrastText' 
                        : 'text.primary',
                    fontWeight: isEdge || isToday ? 600 : 400,
                    border: isToday ? 1 : 0,
                    borderColor: 'primary.main',
                    '&:hover': {
                      bgcolor: isEdge 
                        ? 'primary.dark' 
                        : inRange 
                          ? 'primary.main' 
                          : 'action.hover',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'transparent',
                      color: 'text.disabled',
                    },
                  }}
                >
                  {day || ''}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Кнопки действий */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mt: 3,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
        }}>
          <Button
            size="small"
            onClick={handleClose}
            sx={{ borderRadius: 1 }}
          >
            Отмена
          </Button>
          
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
            disabled={!tempRange.start}
            sx={{ borderRadius: 1 }}
          >
            Применить
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default DateRangePicker;
